import React, { lazy, Suspense } from "react";
import Preloader from "../components/Preloader";

const CHUNK_RELOAD_KEY = "chunk-load-reload";
const CHUNK_RELOAD_INTERVAL_MILLISECONDS = 30 * 1000;

/**
 * A deploy replaces the whole build folder, so the chunks of the previous version are gone. A tab that was
 * already open (or that kept an old index.html) then asks for files that do not exist anymore: the server
 * answers with index.html and the browser fails to parse it as JavaScript ("Unexpected token '<'"), or the
 * page ends up running two different builds ("... is not defined"). The import is retried once and, if it
 * still fails, the page is reloaded so the browser picks up the new index.html - at most once every 30
 * seconds, so a chunk that is really missing cannot put the page in a reload loop.
 */
const shouldReloadForChunk = () => {
  try {
    const lastReload = Number(globalThis.sessionStorage?.getItem(CHUNK_RELOAD_KEY) || 0);

    if (Date.now() - lastReload < CHUNK_RELOAD_INTERVAL_MILLISECONDS) {
      return false;
    }

    globalThis.sessionStorage?.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    // sessionStorage can be unavailable (private mode), the page is then not reloaded
    return false;
  }
};

const importWithRetry = (importFunc) =>
  importFunc().catch((err) =>
    importFunc().catch(() => {
      if (shouldReloadForChunk()) {
        globalThis.location.reload();
        // the reload takes over, so this promise is left pending on purpose
        return new Promise(() => {});
      }

      throw err;
    })
  );

const loadable = (importFunc, fallback) => {
  const LazyComponent = lazy(() => importWithRetry(importFunc));

  return (props) => (
    <Suspense fallback={fallback || <Preloader className="center-on-body" fontSize={50} />}>
      <LazyComponent {...props} />
    </Suspense>
  );
};

export default loadable;
